import classNames from 'classnames'
import { nanoid } from 'nanoid'
import { type ReactNode, useEffect, useRef, useState, type ComponentType, forwardRef } from 'react'
import { type ScrollbarProps, Scrollbars } from 'react-custom-scrollbars-2'
import { getAriaLabel } from 'shared/lib'
import {
  type IScrollbarsPropsRef,
  useScrollbarApiRef,
} from 'shared/ui/Scrollbar/hooks/useScrollbarApiRef'
import { useMutationObserver } from 'shared/ui/Scrollbar/hooks/useMutationObserver'
import styles from './style.module.scss'

export type IScrollbars = Scrollbars

export type IScrollbarProps = ScrollbarProps & {
  children: ReactNode
  hideTrack?: boolean
  viewClassName?: string
  scrollToTOpTrigger?: string
  scrollToBottomTrigger?: boolean
  indentX?: number
}

const ScrollbarRef = forwardRef<IScrollbarsPropsRef, IScrollbarProps>(
  (
    {
      children,
      hideTrack,
      scrollToTOpTrigger,
      viewClassName,
      scrollToBottomTrigger,
      indentX = 0,
      ...props
    },
    ref
  ) => {
    const scrollbarRef = useRef<Scrollbars>(null)
    const [show, setShow] = useState(true)
    const callbackMouseover = () => {
      setShow(false)
    }
    const callbackMouseout = () => {
      setShow(true)
    }

    useScrollbarApiRef(ref, scrollbarRef)
    useMutationObserver(scrollbarRef)

    useEffect(() => {
      if (scrollbarRef.current) {
        scrollbarRef.current.container.addEventListener('mouseover', callbackMouseover)
        scrollbarRef.current.container.addEventListener('mouseout', callbackMouseout)

        scrollbarRef.current.container.style.setProperty('--indent', String(indentX))
      }

      return () => {
        if (scrollbarRef.current) {
          scrollbarRef.current.container.removeEventListener('mouseover', callbackMouseover)
          scrollbarRef.current.container.removeEventListener('mouseout', callbackMouseout)
        }
      }
    }, [])

    useEffect(() => {
      if (scrollbarRef.current) {
        if (scrollToTOpTrigger) {
          scrollbarRef.current.scrollToTop()
        }
      }
    }, [scrollToTOpTrigger])

    useEffect(() => {
      if (scrollbarRef.current) {
        if (scrollToBottomTrigger) {
          scrollbarRef.current.scrollToBottom()
        }
      }
    }, [scrollToBottomTrigger])

    const renderView = (props: ComponentType) => {
      return (
        <div
          {...props}
          className={classNames(styles.view, viewClassName, `scrollbarView-${nanoid(2)}`)}
          aria-label={getAriaLabel('Scrollbar', 'view')}
        />
      )
    }

    const renderTrackHorizontal = (props: ComponentType) => {
      return (
        <div
          {...props}
          aria-label={getAriaLabel('Scrollbar', 'trackHorizontal')}
          className={classNames(styles.trackHorizontal, {
            [styles.hiddenTrack]: hideTrack,
          })}
        />
      )
    }

    const renderThumbHorizontal = (props: ComponentType) => {
      return (
        <div
          {...props}
          aria-label={getAriaLabel('Scrollbar', 'ThumbHorizontal')}
          className={styles.thumbHorizontal}
        />
      )
    }

    const renderTrackVertical = (props: ComponentType) => {
      return (
        <div
          {...props}
          aria-label={getAriaLabel('Scrollbar', 'TrackVertical')}
          className={classNames(styles.trackVertical, {
            [styles.hiddenTrack]: hideTrack,
          })}
        />
      )
    }

    const renderThumbVertical = (props: ComponentType) => {
      return (
        <div
          {...props}
          aria-label={getAriaLabel('Scrollbar', 'ThumbVertical')}
          className={styles.thumbVertical}
        />
      )
    }

    return (
      <Scrollbars
        ref={scrollbarRef}
        autoHide={show}
        autoHideTimeout={300}
        autoHideDuration={200}
        style={{ width: '100%', height: props.height || '100%' }}
        renderView={renderView}
        renderTrackHorizontal={renderTrackHorizontal}
        renderThumbHorizontal={renderThumbHorizontal}
        renderTrackVertical={renderTrackVertical}
        renderThumbVertical={renderThumbVertical}
        aria-label={getAriaLabel('Scrollbar')}
        {...props}
        className={classNames(styles.scrollbars, props.className)}
      >
        {children}
      </Scrollbars>
    )
  }
)

ScrollbarRef.displayName = 'Scrollbar'

export const Scrollbar = ScrollbarRef
